import type { FindAllCarsQueryDto } from '@/entities/cars/car.entity';
import { computed, type Ref } from 'vue';
import { VehiclePoolTab as Tab } from './vehicle-pool-tab.enum';
import { CarStatus } from '@/entities/cars/car-status.enum';
import { DateTime } from 'luxon';
import { FindAllCarsSort } from '@/entities/cars/find-all-cars-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import {
  CarTyres,
  TyreCondition,
  WorkshopAppointmentListType,
  WorkshopAppointmentType,
} from '@carvia/ros-client-types';
import { getCurrentCarTyresSeason } from '@/utils/get-current-car-tyres-season.ts';

export const useCarsQuery = (
  activeTab: Ref<Tab>,
  activeWorkshopSubTab: Ref<WorkshopAppointmentListType>,
  licencePlateFinQuery: Ref<string | null | undefined>,
  stationIds: Ref<string[] | undefined>,
  workshopRequirementReason: Ref<WorkshopAppointmentType | null>,
) => {
  return computed<FindAllCarsQueryDto>(() => {
    const query: FindAllCarsQueryDto = {};
    if (licencePlateFinQuery.value) {
      query.licencePlateFinQuery = licencePlateFinQuery.value ?? undefined;
    } else {
      const currentDateTime = DateTime.now().startOf('day');
      const currentTyresSeason = getCurrentCarTyresSeason();
      switch (activeTab.value) {
        case Tab.ORDERED:
          query.isGhostCar = false;
          query.status = [CarStatus.ORDERED];
          query.sort = FindAllCarsSort.AVAILABLE_FROM;
          query.order = Order.ASC;
          break;
        case Tab.FLOAT_IN:
          query.isGhostCar = false;
          query.status = [CarStatus.FLOAT_IN];
          query.sort = [FindAllCarsSort.MAKE, FindAllCarsSort.MODEL];
          query.order = Order.ASC;
          break;
        case Tab.FLOAT_OUT:
          query.isGhostCar = false;
          query.status = [CarStatus.FLOAT_OUT];
          query.sort = [FindAllCarsSort.MAKE, FindAllCarsSort.MODEL];
          query.order = Order.ASC;
          break;
        case Tab.GHOST_CARS:
          query.isGhostCar = true;
          query.status = [CarStatus.FLOAT_IN];
          query.sort = [FindAllCarsSort.MAKE, FindAllCarsSort.MODEL];
          query.order = Order.ASC;
          break;
        case Tab.MERCHANT_CONSULTATION:
          query.isGhostCar = false;
          query.status = [CarStatus.ORDERED];
          query.isSetmerchantConsultedDate = false;
          query.availableFromDateBefore = currentDateTime.plus({ weeks: 6 }).toISO();
          query.sort = FindAllCarsSort.AVAILABLE_FROM;
          query.order = Order.ASC;
          break;
        case Tab.CAR_INCOMING:
          query.isGhostCar = false;
          query.status = [CarStatus.ORDERED];
          query.isSetregistrationAndOrganizationDoneDate = false;
          query.availableFromDateBefore = currentDateTime.plus({ days: 10 }).toISO();
          query.sort = FindAllCarsSort.AVAILABLE_FROM;
          query.order = Order.ASC;
          break;
        case Tab.WORKSHOP_APPOINTMENTS:
          query.isGhostCar = false;
          query.status = [CarStatus.FLOAT_IN];
          if (activeWorkshopSubTab.value === WorkshopAppointmentListType.REQUIRES_WORKSHOP) {
            if (workshopRequirementReason.value === WorkshopAppointmentType.INSPECTION) {
              query.needInspection = true;
            } else if (workshopRequirementReason.value === WorkshopAppointmentType.TUV) {
              query.needsTuv = true;
            } else {
              query.requiresWorkshop = true;
            }
          }
          query.sort = [FindAllCarsSort.MAKE, FindAllCarsSort.MODEL];
          query.order = Order.ASC;
          break;
        case Tab.TYRE_CONDITION:
          query.isGhostCar = false;
          query.status = [CarStatus.FLOAT_IN];
          query.tyreConditions = [
            TyreCondition.NEAR_WEAR_LIMIT,
            TyreCondition.NEEDS_IMMEDIATE_CHANGE,
          ];
          query.sort = [FindAllCarsSort.TYRE_CONDITION_PRIORITY];
          query.order = Order.DESC;
          break;
        case Tab.TYRES_SEASON:
          query.isGhostCar = false;
          query.status = [CarStatus.FLOAT_IN];
          query.tyres = currentTyresSeason === CarTyres.SUMMER ? CarTyres.WINTER : CarTyres.SUMMER;
          query.isAlwaysOnWinterTyres = false;
          query.sort = [FindAllCarsSort.MAKE, FindAllCarsSort.MODEL];
          query.order = Order.ASC;
          break;
        case Tab.SELL:
          query.isGhostCar = false;
          query.status = [CarStatus.FLOAT_IN];
          query.availableFromDateBefore = currentDateTime.minus({ months: 23 }).toISO();
          query.sort = FindAllCarsSort.AVAILABLE_FROM;
          query.order = Order.ASC;
          break;
      }
    }
    return {
      ...query,
      stationIds: stationIds.value,
      limit: 20,
    };
  });
};
